/**
 * ContractorLogin — public contractor sign-in for /v4/* (no legacy app.html required).
 * Route: /v4/login (BrowserRouter basename /v4)
 */

import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { contractorLogin } from '../../api/auth'
import { useAuth } from '../../context/AuthContext'
import { postAuthHome } from '../../lib/safeNextPath'
import PasswordInput from '../../components/auth/PasswordInput'
import { AuthBrandHeader, AuthFooter } from '../../components/auth/AuthBrandHeader'
import GoogleSignInButton from '../../components/auth/GoogleSignInButton'

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  google_denied: 'Google sign-in was cancelled.',
  google_failed: 'Google sign-in failed. Please try again.',
  account_blocked: 'Your account has been suspended. Please contact support.',
}

export default function ContractorLogin() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [email, setEmail]           = useState(() => searchParams.get('email')?.trim() || '')
  const [password, setPassword]     = useState('')
  const [keepSignedIn, setKeepSignedIn] = useState(true)
  const [error, setError]           = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { refetch, user, loading } = useAuth()

  useEffect(() => {
    if (!loading && user) {
      navigate(postAuthHome(user, searchParams.get('next')), { replace: true })
    }
  }, [loading, user, navigate, searchParams])

  useEffect(() => {
    const authError = searchParams.get('auth_error')
    if (authError && AUTH_ERROR_MESSAGES[authError]) {
      setError(AUTH_ERROR_MESSAGES[authError])
    }
  }, [searchParams])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const result = await contractorLogin(email.trim(), password, keepSignedIn)
      await refetch()
      navigate(
        postAuthHome(
          { ...result.user, needs_portal_choice: result.needs_portal_choice },
          searchParams.get('next'),
          result.redirect_to,
        ),
        { replace: true },
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid email or password')
    } finally {
      setSubmitting(false)
    }
  }

  function handleGoogleSignIn() {
    window.location.href = '/api/auth/google'
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div className="auth-spinner" />
      </div>
    )
  }

  return (
    <div
      data-testid="contractor-login"
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 380 }}>
        <AuthBrandHeader />
        <div style={{
          width: '100%',
          background: '#fff',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r)',
          padding: '28px 24px',
        }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '0 0 6px' }}>
          Contractor sign in
        </h1>
        <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '0 0 22px', lineHeight: 1.5 }}>
          Sign in to manage projects, pay apps, and the project hub.
        </p>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 6 }}>
              Email
            </span>
            <input
              id="login-email"
              name="username"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="fld-i"
              style={{ width: '100%', padding: '10px 12px', fontSize: 14 }}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink3)' }}>
                Password
              </span>
              <Link
                to="/forgot-password"
                style={{ fontSize: 11, color: 'var(--ink4)', textDecoration: 'none', fontWeight: 600 }}
              >
                Forgot password?
              </Link>
            </div>
            <PasswordInput
              id="login-password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ padding: '10px 12px', fontSize: 14 }}
            />
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 18,
            fontSize: 13,
            color: 'var(--ink3)',
            cursor: 'pointer',
            minHeight: 44,
          }}>
            <input
              type="checkbox"
              name="keepSignedIn"
              checked={keepSignedIn}
              onChange={e => setKeepSignedIn(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            Keep me signed in
          </label>

          {error && (
            <p style={{ fontSize: 12, color: 'var(--red)', margin: '0 0 14px' }} role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="contractor-sign-in"
            className="btn b-dk"
            disabled={submitting}
            style={{ width: '100%', justifyContent: 'center', padding: '10px 16px', fontWeight: 600 }}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          margin: '18px 0',
        }}>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
          <span style={{ fontSize: 11, color: 'var(--ink5)', fontWeight: 600 }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
        </div>

        <GoogleSignInButton onClick={handleGoogleSignIn} disabled={submitting} />

        <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '18px 0 0', textAlign: 'center' }}>
          Don&apos;t have an account?{' '}
          <Link to="/register" style={{ color: 'var(--ink3)', textDecoration: 'none', fontWeight: 600 }}>
            Sign up →
          </Link>
        </p>
        </div>
        <AuthFooter />
      </div>
    </div>
  )
}
